/**
 * hearo — Contact Modal Custom Element
 *
 * Global modal triggered by any element with [data-open-contact-modal].
 * Rendered once via snippets/hearo-contact-modal.liquid (included in theme.liquid).
 *
 * Trigger attributes (on the element that opens the modal):
 *   data-open-contact-modal                     required — marks the trigger
 *   data-modal-source="phone-click"             optional — stored in hidden field
 *   data-modal-category="call-request"          optional — stored in hidden field
 *   data-modal-eyebrow="Call-back request"      optional — overrides eyebrow
 *   data-modal-headline="We'll call you back."  optional — overrides headline
 *   data-modal-subheadline="..."                optional — overrides subheadline
 *   data-modal-prefill-message="..."            optional — pre-fills textarea
 *
 * Accessibility:
 *   - Focus-trap (Tab stays inside modal)
 *   - Escape key closes
 *   - Backdrop-click does NOT close (prevents accidental dismiss for seniors)
 *   - Previous focus is restored on close
 *   - aria-modal, aria-labelledby, role=dialog
 *
 * Submit flow (JS-enabled path — the normal case):
 *   - preventDefault on the form submit
 *   - Show loading state on submit button
 *   - POST via fetch to the form's action URL (Shopify /contact)
 *   - On success: hide the form body, show the inline success block
 *   - On error: re-enable submit, show error message
 *   - No page reload → no flash, no delay
 *
 * Fallback flow (JS disabled / fetch fails): standard form submit reloads
 * the page with Shopify's ?contact_posted=true. The Liquid template
 * renders the success block by default in that case, and the
 * sessionStorage flag below re-opens the modal automatically.
 */

(function () {
  'use strict';

  if (window.customElements && customElements.get('hearo-contact-modal')) {
    return;
  }

  const SESSION_FLAG = 'hearo-modal-submitted';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class HearoContactModal extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.previousFocus = null;
      this._onDocClick = this._onDocClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
    }

    connectedCallback() {
      // Wire listeners
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);

      // Bind ALL close buttons (X in header + Close in success state)
      const closeBtns = this.querySelectorAll('[data-close-modal]');
      closeBtns.forEach((btn) => btn.addEventListener('click', this._onCloseClick));

      const form = this.querySelector('form');
      if (form) form.addEventListener('submit', this._onSubmit);

      // Fallback path: if we ended up here via full-page reload after
      // a non-JS submit, re-open the modal with the success block shown.
      try {
        const flagSet = sessionStorage.getItem(SESSION_FLAG) === 'true';
        const successBlock = this.querySelector('.hearo-modal__success');
        const successVisible = successBlock && !successBlock.hasAttribute('hidden');
        if (flagSet && successVisible) {
          sessionStorage.removeItem(SESSION_FLAG);
          requestAnimationFrame(() => this.open({}, { skipAnimation: true }));
        } else if (flagSet) {
          sessionStorage.removeItem(SESSION_FLAG);
        }
      } catch (e) {
        // sessionStorage blocked; fail silent
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
    }

    _onDocClick(e) {
      const trigger = e.target.closest('[data-open-contact-modal]');
      if (!trigger) return;
      e.preventDefault();
      this.open(trigger.dataset);
    }

    _onCloseClick(e) {
      e.preventDefault();
      this.close();
    }

    _onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === 'Tab') {
        this._trapFocus(e);
      }
    }

    /**
     * Intercept form submit — POST via fetch, swap to success state in place.
     * On fetch failure, fall back to native form submit + session-flag reload.
     */
    async _onSubmit(e) {
      const form = e.currentTarget;
      if (!form || !(form instanceof HTMLFormElement)) return;

      // Native HTML5 validity check — bail out of AJAX if invalid so the
      // browser shows its built-in tooltips.
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
        return;
      }

      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
        submitBtn.textContent = 'Sending…';
      }

      try {
        const formData = new FormData(form);
        const action = form.action || '/contact';

        const response = await fetch(action, {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin',
        });

        // Shopify's contact endpoint returns a redirect (302) on success.
        // With redirect: 'follow' (default) the browser lands on the
        // referring page and fetch resolves with response.ok = true.
        // Any non-ok status (4xx, 5xx) or network error → treat as error.
        if (response.ok || response.redirected) {
          this._showSuccessInPlace();
        } else {
          this._showSubmitError();
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            submitBtn.textContent = originalText;
          }
        }
      } catch (err) {
        // Network error — fall back to native submit so the user isn't stuck.
        try {
          sessionStorage.setItem(SESSION_FLAG, 'true');
        } catch (e2) {
          // ignore
        }
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
          submitBtn.textContent = originalText;
        }
        form.submit();
      }
    }

    _showSuccessInPlace() {
      const formBody = this.querySelector('.hearo-modal__form-body');
      const successBlock = this.querySelector('.hearo-modal__success');
      if (formBody) formBody.setAttribute('hidden', '');
      if (successBlock) {
        successBlock.removeAttribute('hidden');
        // Move focus to the success close button for keyboard users.
        const successClose = successBlock.querySelector('[data-close-modal]');
        if (successClose) {
          window.setTimeout(() => {
            try {
              successClose.focus({ preventScroll: true });
            } catch (e) {
              successClose.focus();
            }
          }, 40);
        }
      }
      // Scroll the panel to the top so users see the success headline.
      const panel = this.querySelector('.hearo-modal__panel');
      if (panel) panel.scrollTop = 0;
    }

    _showSubmitError() {
      let errBox = this.querySelector('.hearo-modal__error');
      if (!errBox) {
        errBox = document.createElement('div');
        errBox.className = 'hearo-modal__error';
        errBox.setAttribute('role', 'alert');
        errBox.innerHTML = '<p>We couldn\'t send your message. Please check your email address and try again — or call us directly.</p>';
        const header = this.querySelector('.hearo-modal__header');
        if (header && header.parentNode) {
          header.parentNode.insertBefore(errBox, header.nextSibling);
        }
      }
      errBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    _trapFocus(e) {
      const focusables = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /**
     * Open the modal and populate fields from trigger dataset.
     * Always shows the form body (not the success block) on fresh open —
     * unless server-side rendered that way (fallback reload path).
     */
    open(data = {}, opts = {}) {
      if (this.isOpen) return;

      // Reset to the form state on every fresh trigger open, UNLESS the
      // server already rendered the success state (non-JS fallback).
      const successBlock = this.querySelector('.hearo-modal__success');
      const serverSuccess = successBlock && !successBlock.hasAttribute('hidden');
      if (!serverSuccess) {
        const formBody = this.querySelector('.hearo-modal__form-body');
        if (formBody) formBody.removeAttribute('hidden');
        if (successBlock) successBlock.setAttribute('hidden', '');
        // Reset submit button if it was left in loading state
        const submitBtn = this.querySelector('.hearo-modal__submit');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
          if (submitBtn.dataset.originalText) {
            submitBtn.textContent = submitBtn.dataset.originalText;
          }
        }
      }

      this.previousFocus = document.activeElement;
      this.isOpen = true;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.body.classList.add('hearo-modal-open');

      // Populate hidden fields
      this._setFieldValue('[data-modal-source-field]', data.modalSource);
      this._setFieldValue('[data-modal-category-field]', data.modalCategory);

      // Override visible copy if provided
      this._setTextContent('[data-modal-eyebrow]', data.modalEyebrow);
      this._setTextContent('[data-modal-headline]', data.modalHeadline);
      this._setTextContent('[data-modal-subheadline]', data.modalSubheadline);

      // Pre-fill message textarea if provided
      if (data.modalPrefillMessage) {
        const messageField = this.querySelector('[data-modal-message-field]');
        if (messageField && !messageField.value) {
          messageField.value = data.modalPrefillMessage;
        }
      }

      // Focus the close button first (non-intrusive entry point)
      const focusTarget =
        this.querySelector('[data-modal-initial-focus]') ||
        this.querySelector('.hearo-modal__close') ||
        this.querySelector(FOCUSABLE);

      if (focusTarget) {
        window.setTimeout(() => {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch (e) {
            focusTarget.focus();
          }
        }, 60);
      }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-open');
      document.body.classList.remove('hearo-modal-open');

      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        try {
          this.previousFocus.focus({ preventScroll: true });
        } catch (e) {
          this.previousFocus.focus();
        }
      }
      this.previousFocus = null;
    }

    _setFieldValue(selector, value) {
      if (value === undefined || value === null) return;
      const field = this.querySelector(selector);
      if (field) field.value = value;
    }

    _setTextContent(selector, value) {
      if (value === undefined || value === null || value === '') return;
      const el = this.querySelector(selector);
      if (el) el.textContent = value;
    }
  }

  customElements.define('hearo-contact-modal', HearoContactModal);
})();
